import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	container: {
		paddingBlock: token('space.100'),
	},
});

const messages = defineMessages({
	tableFooterResults: {
		id: 'organization.audit.log.side-panel.tabs.related-activities.table.footer.results',
		defaultMessage: '1-{numOfDisplayedEvents} of {totalEvents} results',
		description: 'Message displaying how results are displayed in side panel mini table',
	},
	goToRelatedLink: {
		id: 'organization.audit.log.side-panel.tabs.related-activities.table.footer.go-to-related.link',
		defaultMessage: 'See all related activities',
		description: 'Link allowing user to open new window and filter audit log by correlation ID',
	},
});

export interface TableFooterProps {
	/** Number of events currently displayed in the table */
	displayedEventsCount: number;
	/** Whether there are more events beyond the current count */
	hasMoreEvents: boolean;
	/** Link to the related events */
	relatedEventsLink: string;
	/** Total number of events available */
	totalEventsCount: number;
}

export const TableFooter = ({
	displayedEventsCount,
	totalEventsCount,
	hasMoreEvents,
	relatedEventsLink,
}: TableFooterProps): React.JSX.Element => {
	if (displayedEventsCount === 0) {
		return <Box />;
	}

	const relatedEventsTotal: string = totalEventsCount + (hasMoreEvents ? '+' : '');

	return (
		<Box xcss={styles.container}>
			<Inline spread="space-between">
				<Text color="color.text.subtle">
					<FormattedMessage
						{...messages.tableFooterResults}
						values={{
							numOfDisplayedEvents: displayedEventsCount,
							totalEvents: relatedEventsTotal,
						}}
					/>
				</Text>
				<Link href={relatedEventsLink} target="_blank">
					<FormattedMessage {...messages.goToRelatedLink} />
				</Link>
			</Inline>
		</Box>
	);
};
